import React, { useEffect, useState } from "react";
import { Form, Image, Select, Upload, Result } from "antd";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { CheckKycStatusApi, UploadDocumentApi } from "../../../../api/request/kyc.js";
import { setUserDetails } from "../../../../store/slices/userSlice.js";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";

const KycForm = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [reqRejectionReason, setReqRejectionReason] = useState(null);
  const [initialValues] = useState({
    KYCDocumentType1: null,
    KYCDocument: null,
    Signature: null,
  });
  const [documentOption] = useState([
    { value: 1, label: "Passport" },
    { value: 2, label: "National ID" },
    { value: 3, label: "Driving License" },
    { value: 4, label: "Utility Bill" },
    { value: 5, label: "Bank Statement" },
  ]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [fileList, setFileList] = useState([]);
  const [signatureFileList, setSignatureFileList] = useState([]);
  const [documentType, setDocumentType] = useState(null);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    form.setFieldsValue({ KYCDocument: newFileList });
  };

  const handleSignatureChange = ({ fileList: newSignatureList }) => {
    setSignatureFileList(newSignatureList);
    form.setFieldsValue({ Signature: newSignatureList });
    if (newSignatureList?.length === 0) {
      form.setFieldsValue({ Signature: null });
    }
  };

  const handleDocumentTypeChange = (value) => {
    setDocumentType(value);
  };

  const UploadButton = (
    <button type="button">
      <div className="flex justify-center">
        <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" />
      </div>
      Upload
    </button>
  );

  const handleSubmit = async (values) => {
    const extractOriginFileObjs = (fileArray) => {
      return fileArray.map((file) => file.originFileObj);
    };

    const selectedDocumentType = form.getFieldValue("KYCDocumentType1");
    const requiresBothSides = selectedDocumentType === 1 || selectedDocumentType === 2;
    const uploadedDocuments = extractOriginFileObjs(values?.KYCDocument);

    if (requiresBothSides && uploadedDocuments.length !== 2) {
      toast.error("Please upload both sides of the selected document.");
      return;
    }

    setIsLoading(true);
    try {
      let formData = new FormData();
      formData.append("KYCDocumentType", values?.KYCDocumentType1);

      uploadedDocuments.forEach((file) => {
        formData.append("KYCDocument", file);
      });

      extractOriginFileObjs(values?.Signature)?.forEach((file) => {
        formData.append("Signature", file);
      });

      let apiHeaders = {
        headers: { Accept: "application/json", "Content-Type": "multipart/form-data" },
      };
      const { data } = await UploadDocumentApi(formData, apiHeaders);

      if (data?.success) {
        form.resetFields();
        setSignatureFileList([]);
        setFileList([]);
        dispatch(setUserDetails({ ...userDetails, status: "Pending" }));
        toast.success(data?.message);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectedRequest = async () => {
    try {
      let params = { UserId: userDetails?.id };
      const { data } = await CheckKycStatusApi(new URLSearchParams(params).toString());
      if (data?.success) {
        dispatch(
          setUserDetails({
            ...userDetails,
            status: data?.data?.status,
            kycStatus: data?.data?.kycStatus,
          })
        );
        setReqRejectionReason(data?.data?.comment);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (userDetails?.status === "Declined") {
      handleRejectedRequest();
    }
  }, []);

  const maxFiles = documentType === 1 || documentType === 2 ? 2 : 1;

  // ✅ Show success screen if KYC approved
  if (userDetails?.status === "Approved") {
    return (
      <Result
        status="success"
        title="Your KYC is approved successfully!"
        subTitle="Thank you for completing your verification."
      />
    );
  }

  return (
    <div className="kyc-container">
      <div className="container grid md:grid-cols-2 grid-cols-1 justify-items-center items-center gap-5 p-5 mx-auto mt-5">
        <div className="grid grid-cols-1 gap-8 w-full">
          {reqRejectionReason && (
            <div>
              <p className="font-semibold text-red-600 text-xl mb-2">
                Your KYC request is rejected because of the following reason:
              </p>
              <p
                className="text-md"
                dangerouslySetInnerHTML={{ __html: reqRejectionReason }}
              ></p>
            </div>
          )}

          <div>
            <p className="font-bold text-[#2F2B3D] sm:text-4xl text-xl">
              Please Submit Your KYC
            </p>
          </div>

          <Form
            autoComplete="off"
            form={form}
            initialValues={initialValues}
            onFinish={handleSubmit}
          >
            {/* Document Type */}
            <div className="max-w-[480px] ant-select-selector-white">
              <label className="text-base text-[#2F2B3DCC] font-medium">
                Document Type
              </label>
              <Form.Item
                name="KYCDocumentType1"
                rules={[{ required: true, message: "Please select a document type." }]}
              >
                <Select
                  className="w-full mt-1"
                  placeholder="Select document type"
                  options={documentOption}
                  onChange={handleDocumentTypeChange}
                />
              </Form.Item>
            </div>

            {/* Upload Document */}
            <div className="mt-5 ant-upload-drag-wrapper">
              <label className="text-base text-[#2F2B3DCC] font-medium">
                Upload Document
              </label>
              <Form.Item
                name="KYCDocument"
                rules={[{ required: true, message: "Please upload KYC documents." }]}
              >
                <Upload
                  beforeUpload={() => false}
                  listType="picture-card"
                  className="mt-1"
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                >
                  {fileList.length >= maxFiles ? null : UploadButton}
                </Upload>
              </Form.Item>
              {previewImage && (
                <Image
                  wrapperStyle={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) => !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              )}
            </div>

            {/* Upload Selfie */}
            <div className="mt-5 ant-upload-drag-wrapper">
              <label className="text-base text-[#2F2B3DCC] font-medium">
                Upload Selfie
              </label>
              <Form.Item
                name="Signature"
                rules={[{ required: true, message: "Please upload your Selfie." }]}
              >
                <Upload
                  beforeUpload={() => false}
                  listType="picture-card"
                  className="mt-1"
                  fileList={signatureFileList}
                  onPreview={handlePreview}
                  onChange={handleSignatureChange}
                >
                  {signatureFileList.length >= 1 ? null : UploadButton}
                </Upload>
              </Form.Item>
              {previewImage && (
                <Image
                  wrapperStyle={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) => !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />
              )}
            </div>

            <div className="mt-10">
              <LoadableButton
                className="bg-primary text-white text-base font-semibold rounded-lg px-12 py-2 themeHover duration-500"
                disabled={isLoading}
                type="Submit"
                lable="Send Request"
                isLoading={isLoading}
                loadingLable="Sending Request..."
              />
            </div>
          </Form>
        </div>

        <div>
          <img src="/assets/images/kyc.png" alt="" className="w-[35rem] p-4" />
        </div>
      </div>
    </div>
  );
};

export default KycForm;
